import React, { useState, useEffect , useRef} from 'react';
import PropTypes from 'prop-types';
import Map, { Marker, Popup, NavigationControl, Source, Layer} from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import { t } from 'i18next';
import ActionButton from './ActionButton';
import Constants from '../utils/constants';
import { InputNumber } from 'primereact/inputnumber';
import StringUtils from '../utils/StringUtils';

export const LocationComponent = ({locations, onAddLocation, onUpdateLocation, onUpdateLocationTolerance, viewMode, showLocationTolerance, showErrorMessage, initLocation, style}) => {
    
    const zoomLevelInit = locations.length === 0 ? 5 : 10;
    const [zoomLevel, setZoomLevel] = useState(zoomLevelInit);
    const [selectedMarker, setSelectedMarker] = useState(null);
    const mapRef = useRef(null); 

    useEffect(() => {
        if(mapRef.current){
            mapRef.current.setCenter([initLocation[1], initLocation[0]]); 
        }
    }, [initLocation]);
  
    useEffect(() => {
    }, [zoomLevel, locations]);

    const addMarker = (event) => {
        if (viewMode !== 'VIEW') {
            const lat = event.lngLat.lat;
            const lng = event.lngLat.lng;
            const precision = 18;
            const existingLocation = locations.find(
                (location) =>
                    parseFloat(location.latitude).toFixed(precision) === lat.toFixed(precision) &&
                    parseFloat(location.longitude).toFixed(precision) === lng.toFixed(precision)
            );
            if (existingLocation) {
                showErrorMessage(t("global.locationAlreadyAdded"));
                return;
            }
            const newMarker = {
                latitude: lat,
                longitude: lng,
                id: undefined,
            };
            onAddLocation(newMarker);
        }
    };

    const handleLocationToleranceChange = (index, event) =>{
        if(onUpdateLocationTolerance){
            const updatedMarkers = [...locations];
            updatedMarkers[index].locationToleranceInKilometers = event.value;
            onUpdateLocationTolerance(updatedMarkers);
        }
    }

    const handleMarkerDrag = (index, event) => {

        if (viewMode !== 'VIEW') {
            const lat = event.lngLat.lat;
            const lng = event.lngLat.lng;
            const updatedMarkers = [...locations];
            updatedMarkers[index] = {
                ...updatedMarkers[index],
                latitude: lat,
                longitude: lng,
            };
            onUpdateLocation(updatedMarkers);
        }
    };

    const handleZoomChange = (event) => {
        const newZoomLevel =event.viewState.zoom;
        setZoomLevel(newZoomLevel);
    };

    const removeMarker = (index) => {
        const updatedMarkers = [...locations];
        updatedMarkers.splice(index, 1);
        setSelectedMarker(undefined)
        onUpdateLocation(updatedMarkers);
    };

    const metersToPixels = (meters, latitude, zoom) => {
        const earthCircumference = 40075017; 
        const latitudeRadians = latitude * (Math.PI / 180);
        const metersPerPixel = earthCircumference * Math.cos(latitudeRadians) / Math.pow(2, zoom + 8); // Przeliczamy piksele
        const result = meters / metersPerPixel;
        return result;
    };
    
    return (
        <div className="row">
            
            <div className={'col-12'}>
                <Map
                    ref={mapRef}
                    initialViewState={{
                        longitude: initLocation[1],
                        latitude: initLocation[0],
                        zoom: zoomLevel,
                    }}            
                    onZoom={handleZoomChange}
                    style={style}
                    mapStyle={process.env.REACT_APP_MAPLIBRE_MAP_STYLE}
                    onClick={addMarker}
                >
                    <NavigationControl position="top-right" />
                    {locations.map((location, index) => {
                       
                       const locationToleranceInKilometers = StringUtils.isBlank(location?.locationToleranceInKilometers) ? 0 : location?.locationToleranceInKilometers;
                     return <React.Fragment key={index}>
                            <Marker
                                key={index}
                                longitude={location.longitude}
                                latitude={location.latitude}
                                draggable={viewMode !== 'VIEW'}
                                onDragEnd={(event) => {
                                    handleMarkerDrag(index, event)}}
                                onClick={(e) => {
                                    e.originalEvent.stopPropagation()
                                    e.originalEvent.preventDefault()
                                    setSelectedMarker(index)
                                }}
                            />
                            {showLocationTolerance && <Source 
                                id={`circles-source-${index}`} 
                                type="geojson" 
                                data={{
                                type: "FeatureCollection",
                                features: [
                                    {
                                        type: "Feature",
                                        geometry: {
                                            type: "Point",
                                            coordinates: [location.longitude, location.latitude]
                                        },
                                        properties: {
                                            tolerance: locationToleranceInKilometers * 1000 
                                        }
                                    }
                                ]
                                }}>
                                <Layer
                                    id={`circle-layer-${index}`}
                                    type="circle"
                                    paint={{
                                        'circle-radius': [
                                            'interpolate', ['linear'], ['zoom'],
                                            zoomLevel, metersToPixels(locationToleranceInKilometers * 1000, locations[index].latitude, zoomLevel)
                                        ],
                                        'circle-color': 'rgba(66, 135, 245, 0.4)', 
                                        'circle-stroke-color': '#4287f5',       
                                        'circle-stroke-width': 2
                                    }}
                                />
                            </Source>}
                               
                      </React.Fragment>   
                    })}
                      {(selectedMarker !== null && locations[selectedMarker] ) && (
                        <Popup
                            longitude={locations[selectedMarker]?.longitude}
                            latitude={locations[selectedMarker]?.latitude}
                            onClose={() => setSelectedMarker(null)}
                        >
                            
                            <div className='p-1'>
                             {showLocationTolerance && <React.Fragment>
                                <label htmlFor='tolerance' className='font-bold block mb-2'>
                                    {t("global.distanceToleranceInKilometers")}
                                </label>
                                <InputNumber
                                    inputId='tolerance'
                                    minFractionDigits={2}
                                    min={0}                           
                                    type='text'
                                    disabled={viewMode === 'VIEW'}
                                    label={t("global.tolerance")}
                                    className='mb-2'
                                    locale='pl-PL'
                                    value={StringUtils.isBlank(locations[selectedMarker]?.locationToleranceInKilometers) ? 0.0 : locations[selectedMarker]?.locationToleranceInKilometers}
                                    onChange={(e) => handleLocationToleranceChange(selectedMarker, e)}

                                />
                                </React.Fragment>}   
                            </div>
                            <div className='p-1'>
                              {viewMode !== 'VIEW' && <ActionButton
                                    label={t("global.remove")}
                                    handleClick={() => removeMarker(selectedMarker)}
                                    className={'float-right delete-btn'}
                                />}  
                            </div>
                        </Popup>
                    )}
                </Map>
            </div>
          
        </div>
    );
};

LocationComponent.defaultProps = {
    viewMode: 'VIEW',
    locations: [],
    style: { width: '100%', height: '500px', zIndex: '100' },
    showAddressPanel: false,
    showLocationTolerance: false,
    onUpdateLocationTolerance: undefined,
    initLocation: Constants.POLAND_LOCATION ,
};

LocationComponent.propTypes = {
    viewMode: PropTypes.string,
    initLocation: PropTypes.array,
    showAddressPanel: PropTypes.bool,
    showLocationTolerance: PropTypes.bool,
    onAddLocation: PropTypes.func.isRequired,
    showErrorMessage: PropTypes.func.isRequired,
    onUpdateLocationTolerance: PropTypes.func,
    style: PropTypes.object,
    onUpdateLocation: PropTypes.func.isRequired,
    locations: PropTypes.array,
};

export default LocationComponent;
